/**
 * AstorOfferBanner
 *
 * Page-level Astor explainer banner for the Offers compare grid. Renders
 * `AstorsReadCard` (variant=summary) with an interpolated narrative that
 * includes inline `<strong>` spans for key numbers (winner lender, monthly
 * savings, break-even month). The numeric spans are kept as React children
 * (NOT serialized to HTML) so they survive translation: `t()` callers receive
 * a placeholder template with `{winner}` / `{savings}` / `{breakEven}` tokens
 * and we splice strong-styled spans back into the rendered output.
 *
 * Confidence + "Show reasoning trace" action are rendered via the card's
 * built-in slots so we get the mint glow + sparkle eyebrow for free.
 */

import { AstorsReadCard, Box, GlassButton, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement, ReactNode } from 'react';

export interface AstorOfferBannerProps {
  /** Winner offer lender — bolded in the narrative. */
  winnerLender?: string;
  /** Monthly savings amount (formatted currency string). */
  savingsFormatted?: string;
  /** Break-even month number. */
  breakEvenMonth?: number | null;
  /** 0-100 confidence. */
  confidencePercent?: number;
  /** Tool slugs invoked by Astor. */
  tools?: string[];
  /** Reasoning-trace handler. */
  onShowReasoning?: () => void;
}

/**
 * Renders the narrative body with inline bold spans for the dynamic numbers.
 * We avoid `dangerouslySetInnerHTML` so we can use translated strings that
 * embed `{winner}` / `{savings}` / `{breakEven}` placeholder tokens — the
 * renderer interpolates real React elements back into the gaps.
 */
function renderNarrative(template: string, parts: Record<string, ReactNode>): ReactNode[] {
  const segments = template.split(/(\{[a-zA-Z]+\})/g);
  return segments.map((segment, idx) => {
    const match = segment.match(/^\{([a-zA-Z]+)\}$/);
    const key = `seg-${idx}`;
    if (match?.[1] && parts[match[1]] !== undefined) {
      return <span key={key}>{parts[match[1]]}</span>;
    }
    return <span key={key}>{segment}</span>;
  });
}

export function AstorOfferBanner({
  winnerLender,
  savingsFormatted,
  breakEvenMonth,
  confidencePercent,
  tools,
  onShowReasoning,
}: AstorOfferBannerProps): ReactElement | null {
  const { t } = useI18n();

  // No banner when we have no winner to recommend.
  if (!winnerLender || !savingsFormatted) return null;

  // i18n template uses placeholder tokens so translators keep the structure.
  // Fallback English: "{winner} is cheaper by {savings}/mo — and you break even on the upfront fees by month {breakEven}."
  const template = t('consumer.offers.astorBanner.body');
  const bold = (value: ReactNode) => (
    <Typography component="strong" sx={{ fontWeight: 700, color: 'success.main' }}>
      {value}
    </Typography>
  );
  const narrative = renderNarrative(template, {
    winner: bold(winnerLender),
    savings: bold(savingsFormatted),
    breakEven: bold(breakEvenMonth ?? '—'),
  });

  return (
    <Box sx={{ mb: 3 }}>
      <AstorsReadCard
        variant="summary"
        narrative={narrative}
        confidencePercent={confidencePercent}
        tools={tools}
        action={
          onShowReasoning ? (
            <GlassButton size="small" variant="text" onClick={onShowReasoning}>
              {t('consumer.offers.astorBanner.reasoning')}
            </GlassButton>
          ) : undefined
        }
      />
    </Box>
  );
}

AstorOfferBanner.displayName = 'AstorOfferBanner';
